import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@akuna/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useAddPrescricao, useRegistrarDose } from "@/hooks/useHospitalization";
import { toast } from "sonner";
import { Plus, Syringe } from "lucide-react";

const schema = z.object({
  medicamento: z.string().optional(),
  dose: z.string().optional(),
  via: z.string().optional(),
  frequencia_h: z.string().optional(),
  observacoes: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

interface Dose {
  id: string;
  administrado_at: string;
}

interface Prescricao {
  id: string;
  medicamento: string;
  dose: string;
  via: string;
  frequencia_h: number;
  ativa: boolean;
  observacoes?: string | null;
  hospitalization_doses: Dose[];
}

interface MedicamentosTabProps {
  hospitalizationId: string;
  customerId: string;
  prescricoes: Prescricao[];
}

export function MedicamentosTab({ hospitalizationId, customerId, prescricoes }: MedicamentosTabProps) {
  const { t } = useTranslation("hospitalization");
  const [showForm, setShowForm] = useState(false);
  const addPrescricao = useAddPrescricao();
  const registrarDose = useRegistrarDose();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { medicamento: "", dose: "", via: "", frequencia_h: "", observacoes: "" },
  });

  async function onSubmit(values: FormValues) {
    try {
      await addPrescricao.mutateAsync({
        hospitalization_id: hospitalizationId,
        customer_id: customerId,
        medicamento: values.medicamento,
        dose: values.dose,
        via: values.via,
        frequencia_h: parseInt(values.frequencia_h),
        observacoes: values.observacoes || null,
      });
      toast.success(t("success.prescricaoSaved"));
      form.reset();
      setShowForm(false);
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  async function handleDose(prescricaoId: string) {
    try {
      await registrarDose.mutateAsync({
        prescricao_id: prescricaoId,
        hospitalization_id: hospitalizationId,
        customer_id: customerId,
      });
      toast.success(t("success.doseSaved"));
    } catch {
      toast.error(t("errors.saveFailed"));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setShowForm((v) => !v)}>
          <Plus className="h-4 w-4 mr-1" />
          {t("medicamentos.novaPrescricao")}
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="pt-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="medicamento"
                    render={({ field }) => (
                      <FormItem className="col-span-2">
                        <FormLabel>{t("medicamentos.medicamento")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="dose"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("medicamentos.dose")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="via"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("medicamentos.via")}</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="frequencia_h"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("medicamentos.frequencia", { h: "?" })}</FormLabel>
                        <FormControl>
                          <Input type="number" min="1" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="observacoes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t("evolucao.observacoes")}</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    {t("medicamentos.cancelar")}
                  </Button>
                  <Button type="submit" disabled={addPrescricao.isPending}>
                    {t("medicamentos.salvar")}
                  </Button>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      )}

      {prescricoes.length === 0 && !showForm && (
        <p className="text-sm text-muted-foreground text-center py-8">
          {t("medicamentos.semPrescricoes")}
        </p>
      )}

      {prescricoes.map((p) => (
        <Card key={p.id}>
          <CardHeader className="pb-2 pt-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">{p.medicamento}</CardTitle>
              <Badge variant={p.ativa ? "default" : "secondary"}>
                {p.ativa ? t("medicamentos.ativa") : t("medicamentos.encerrada")}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pb-3 space-y-2">
            <p className="text-sm text-muted-foreground">
              {p.dose} · {p.via} · {t("medicamentos.frequencia", { h: p.frequencia_h })}
            </p>
            {p.observacoes && (
              <p className="text-xs text-muted-foreground">{p.observacoes}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {t("medicamentos.doseRegistrada")}: {p.hospitalization_doses.length}
            </p>
            {p.ativa && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleDose(p.id)}
                disabled={registrarDose.isPending}
              >
                <Syringe className="h-3 w-3 mr-1" />
                {t("medicamentos.registrarDose")}
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
